/**
 * GATE ON A PERMISSION, NEVER ON A GROUP NAME.
 *
 * The frontend's checks are for usability only: every act is authorised again
 * on the server. The permission catalogue is the backend's; the names below are
 * the ones core surfaces already gate on in the POC, and the list grows as each
 * module's OpenAPI arrives.
 *
 * CURRENT, 30-09-2026: supplied branch permissions are authoritative. Missing
 * or empty grants offer no protected screens. Unknown strings grant no known
 * action; role names never imply access. The backend still confirms vocabulary.
 *
 * This replaces the former allow-all fallback. The tradeoff and remaining
 * vocabulary confirmation are in DECISIONS.md (30-09-2026) and Settings GAPS B5.
 */
import type { Session } from "./session";

export const PERMISSIONS = [
  "dashboard.view",
  "dispense.view",
  "dispense.override",
  "pos.operate",
  "pos.refund",
  "pos.dayend",
  "inventory.view",
  "inventory.manage",
  "inventory.adjust",
  "patients.view",
  "patients.edit",
  "reports.view",
  "financials.view",
  "register.sign",
  "admin.staff",
  "admin.access",
  "admin.company",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Acts that need the PERSON to be a declared registered pharmacist, whatever
 * their group grants. Being registered is a fact about a human being; a group
 * is a job title a pharmacy chose.
 */
export const NEEDS_REGISTRATION: ReadonlySet<Permission> = new Set(["register.sign", "dispense.override"]);

export function meetsRegistration(permission: Permission, registeredPharmacist: boolean): boolean {
  return !NEEDS_REGISTRATION.has(permission) || registeredPharmacist;
}

/** Signed in, not expired, granted, and registered where the act needs it. */
export function can(
  session: Session | null | undefined,
  permission: Permission,
  now: Date = new Date(),
): boolean {
  if (!session) return false;
  // Absent is not zero: no expiry stated means the server did not say, and the
  // server ends the session by answering 401 — not this clock.
  if (session.expiresAt !== null && new Date(session.expiresAt).getTime() <= now.getTime()) {
    return false;
  }
  if (!session.permissionsSupplied) return false;
  if (!session.permissions.includes(permission)) return false;
  return meetsRegistration(permission, session.registeredPharmacist);
}
